import { getDynamicsFilters } from "@/Groups/Scanner/private/api/getDynamicsFilters";
import { getImpulsesFilters } from "@/Groups/Scanner/private/api/getImpulsesFilters";
import { getChartData } from "@/Groups/Scanner/private/api/getChartData";
import { getCoinsList } from "@/Groups/Scanner/private/api/getCoinsList";
import { getCoinDetails } from "@/Groups/Scanner/private/api/getCoinDetails";

import { BASE_COINS_LIST, CATEGORIES_LIST } from "@/Groups/Scanner/private/constants/config";

import widget from './modules/widget';

const Scanner = {
  namespaced: true,

  modules: {
    widget,
  },

  state: {
    coins: [],
    chartData: [],

    activeCoinsDetail: {
      coinId: null,
      coinData: null,
      dataPeriod: [],
      isMobilePopupOpen: false,
    },

    filter: {
      dynamicsList: [],
      impulsesList: [],
      baseCoinId: {
        global: 'BTC',
        coinDetails: 'USD',
      },
      impulse: {
        context: 'impulse',
        value: null,
      },
      category: 'crypto_100',
      isBriefcase: false,
    },

    pagination: {
      currentPage: 1,
      totalPages: 0,
      hasNextPage: false,
      isLoadingNext: false,
    },

    loaders: {
      dynamicsFilterLoading: false,
      impulsesFilterLoading: false,
      chartDataLoading: false,
      coinsLoading: false,
      isCoinDetailsLoading: false,
    },

    ready: {
      isReadyChart: false,
      isReadyCoins: false,
      isCoinDetailsLoaded: false,
    },

    preselectedImpulseId: null,
  },

  actions: {
    //=====================================================================================
    // Инициализация
    //=====================================================================================
    async init({ state, dispatch }) {
      await Promise.all([
        dispatch('initDynamics'),
        dispatch('initImpulses'),
        dispatch('initChart'),
      ]);
      await dispatch('setInitialImpulse');    
      await dispatch('loadCoinsList');
      await dispatch('applyPreselectedImpulse');
      if (state.coins.length > 0) {
        await dispatch('loadCoinDetails', state.coins[0]);
      }
    },

    async initDynamics({ commit, dispatch }) {
      commit('SET_DYNAMICS_LOADING_FLAG', true);
      const obj = await dispatch('_getCtxFilters');
      const data = await getDynamicsFilters(obj);
      console.log(data)
      commit('SET_DYNAMICS_FILTERS', data);
      commit('SET_DYNAMICS_LOADING_FLAG', false);
    },

    async initImpulses({ commit, dispatch }) {
      commit('SET_IMPULSES_LOADING_FLAG', true);
      const obj = await dispatch('_getCtxFilters');
      const data = await getImpulsesFilters(obj);
      commit('SET_IMPULSES_FILTERS', data);
      commit('SET_IMPULSES_LOADING_FLAG', false);
    },

    async initChart({ commit, state }) {
      commit('SET_CHART_DATA_LOADING_FLAG', true);
      const data = await getChartData({ coin_slug: state.filter.baseCoinId.global });
      commit('SET_CHART_DATA', data);
      commit('SET_CHART_DATA_LOADING_FLAG', false);
      commit('SET_READY_CHART_FLAG', true);
    },

    //Установка начального импульса
    setInitialImpulse({ state, commit }) {
      const impulsesList = state.filter.impulsesList;
      if (!impulsesList?.length) return;
      const dynamicImpulses = impulsesList
        .filter(item => item && item.in_dynamic_list)
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      if (dynamicImpulses.length > 0) {
        const newestImpulse = dynamicImpulses[0];
        commit('SET_IMPULSE', { 
          context: 'impulse', 
          value: newestImpulse.impulse_id 
        });
      }
    },

    _getCtxFilters({ state }) {
      const context = BASE_COINS_LIST[state.filter.baseCoinId.global];
      return { base_coin_id: context.id, type: context.type };
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

    //=====================================================================================
    // Фильтрация
    //=====================================================================================
    async setActiveFilterContext({ state, dispatch, commit }, { slug, ctx }) {
      const isGlobal = (ctx === 'global');
      if (isGlobal) {
        commit('SET_BASE_COIN_ID_GLOBAL', slug);
        await Promise.all([
          dispatch('initImpulses'),
          dispatch('initDynamics'),
          dispatch('initChart'),
        ]);
        await dispatch('loadCoinsList');
        if (state.coins.length > 0) {
          await dispatch('loadCoinDetails', state.coins[0]);
        }
      }
      commit('SET_BASE_COIN_ID_COIN_DETAILS', slug);
      if (state.activeCoinsDetail.coinData) {
        await dispatch('loadCoinDetails', state.activeCoinsDetail.coinData);
      }
    },

    async setCategoryFilter({ commit, dispatch, state }, categorySlug) {
      commit('SET_CATEGORY', categorySlug);

      if (categorySlug === 'briefcase') {
        await dispatch('loadCoinsList');
        if (state.coins.length > 0) {
          await dispatch('loadCoinDetails', state.coins[0]);
        }
        return;
      }

      let newContext;
      if (['crypto_100', 'crypto_300'].includes(categorySlug)) {
        newContext = 'BTC';
      } else if (['top_assets', 'usa', 'commodities'].includes(categorySlug)) {
        newContext = 'SP500';
      } else if (categorySlug === 'ru') {
        newContext = 'IMOEX';
      }
      
      if (newContext && state.filter.baseCoinId.global !== newContext) {
        commit('SET_BASE_COIN_ID_GLOBAL', newContext);
        await Promise.all([
          dispatch('initImpulses'),
          dispatch('initChart'),
        ]);
      }
      
      await dispatch('loadCoinsList');
      if (state.coins.length > 0) {
        await dispatch('loadCoinDetails', state.coins[0]);
      }
    },

    async setImpulseFilter({ state, dispatch, commit }, { context, value }) {
      commit('SET_IMPULSE', { context, value });
      await dispatch('loadCoinsList');
      if (state.coins.length > 0) {
        await dispatch('loadCoinDetails', state.coins[0]);
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

    //=====================================================================================
    // Пагинация
    //=====================================================================================
    async loadCoinsList({ commit, state, dispatch }, { isNext = false } = {}) {
      const loadingFlag = isNext ? 'SET_LOADING_NEXT_FLAG' : 'SET_COINS_LOADING_FLAG';
      const isImpulseActive = state.filter.impulse.context === 'impulse';
      const impulseValue = state.filter.impulse.value;
      const { base_coin_id, type } = await dispatch('_getCtxFilters');
      commit(loadingFlag, true);
      
      // Теперь briefcase тоже в CATEGORIES_LIST, поэтому просто берем оттуда
      const categoryParam = CATEGORIES_LIST[state.filter.category].param;
      
      const params = {
        impulse_type: isImpulseActive ? 'impulse' : 'period-dyn',
        impulse_id: isImpulseActive ? `impulse_id=${impulseValue}` : `period=${impulseValue}`,
        base_asset_id: base_coin_id,
        type: type,
        page: isNext ? state.pagination.currentPage + 1 : 1,
        category: categoryParam, 
      };
      
      const data = await getCoinsList(params);
      if (isNext) {
        commit('APPEND_COINS', data.results);
      } else {
        commit('SET_COINS', data.results);
      }
      commit('SET_PAGINATION', {
        currentPage: data.current || params.page,
        totalPages: data.total_pages || 0,
        hasNextPage: !!data.next
      });
      commit('SET_READY_COINS_FLAG', true);
      commit(loadingFlag, false);
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

    //=====================================================================================
    // Детали монеты
    //=====================================================================================
    async loadCoinDetails({ commit, state, dispatch }, coin) {
      if (!coin?.id) return;

      commit('SET_COIN_DETAILS_LOADING_FLAG', true);
      commit('SET_COIN_DETAILS_ID', coin);
      
      try {
        const { compare_coin_id, compare_type } = await dispatch('_getCtxCoinDetailsFilters');
        const category = state.filter.category;
        
        const compareType = CATEGORIES_LIST[category]?.assetType || 'coin';
        
        const baseParams = {
          base_asset_id: compare_coin_id,
          base_asset_type: compare_type,
          compare_asset_id: coin.id,
          compare_asset_type: compareType,
        };

        const [periodData] = await Promise.all([
          getCoinDetails({ ...baseParams, impulse_type: 'period-dyn' }),
        ]);

        commit('SET_COIN_DETAILS_DATA', { period: periodData });
        commit('SET_COIN_DETAILS_LOADED_FLAG', true);
      } catch (error) {
        console.error('Ошибка загрузки деталей монеты:', error);
      } finally {
        commit('SET_COIN_DETAILS_LOADING_FLAG', false);
      }
    },

    clearCoinDetails({ commit }) {
      commit('CLEAR_COIN_DETAILS');
    },

    _getCtxCoinDetailsFilters({ state }) {
      const context = BASE_COINS_LIST[state.filter.baseCoinId.coinDetails];
      return { compare_coin_id: context.id, compare_type: context.type };
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

    //=====================================================================================
    // Виджет - предвыбор импульса
    //=====================================================================================
    setPreselectedImpulseId({ commit }, impulseId) {
      commit('SET_PRESELECTED_IMPULSE_ID', impulseId);
    },

    async applyPreselectedImpulse({ state, commit, dispatch }) {
      if (state.preselectedImpulseId) {
        await dispatch('setImpulseFilter', {
          context: 'impulse',
          value: state.preselectedImpulseId
        });
        commit('SET_PRESELECTED_IMPULSE_ID', null);
      }
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX


    //=====================================================================================
    // ПЕРЕКЛЮЧЕНИЕ ПОПАПА
    //=====================================================================================
    openMobilePopup({ commit }) {
      commit('SET_MOBILE_POPUP_OPEN', true);
    },

    closeMobilePopup({ commit }) {
      commit('SET_MOBILE_POPUP_OPEN', false);
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX


    //=====================================================================================
    // Сброс состояния
    //=====================================================================================
    resetStore({ commit }) {
      commit('RESET_STATE');
    },
    //XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX
  },

  mutations: {
    //Фильтры для динамики
    SET_DYNAMICS_FILTERS(state, data) { state.filter.dynamicsList = data; },

    //Фильтры для импульсов
    SET_IMPULSES_FILTERS(state, data) { state.filter.impulsesList = data; },

    //Данные графика
    SET_CHART_DATA(state, data) { state.chartData = data; },

    //Базовые ID монет
    SET_BASE_COIN_ID_GLOBAL(state, coinSlug) { state.filter.baseCoinId.global = coinSlug; },
    SET_BASE_COIN_ID_COIN_DETAILS(state, coinSlug) { state.filter.baseCoinId.coinDetails = coinSlug; },

    //Активная категория
    SET_CATEGORY(state, categorySlug) { 
      state.filter.category = categorySlug;
      state.filter.isBriefcase = (categorySlug === 'briefcase');
    },

    //Импульс (динамика/импульс + значение)
    SET_IMPULSE(state, { context, value }) {
      console.log(context, value)
      state.filter.impulse.context = context;
      state.filter.impulse.value = value;
    },

    //Список монет
    SET_COINS(state, coins) { state.coins = coins; },
    APPEND_COINS(state, coins) { state.coins = [...state.coins, ...coins]; },
    CLEAR_COINS(state) { state.coins = []; state.pagination.currentPage = 1; },

    //Пагинация
    SET_PAGINATION(state, { currentPage, totalPages, hasNextPage }) {
      state.pagination.currentPage = currentPage;
      state.pagination.totalPages = totalPages;
      state.pagination.hasNextPage = hasNextPage;
    },

    //Детали монеты
    SET_COIN_DETAILS_ID(state, coin) {
      state.activeCoinsDetail.coinId = coin.id; 
      state.activeCoinsDetail.coinData = coin;
    },

    SET_COIN_DETAILS_DATA(state, { period }) { 
      state.activeCoinsDetail.dataPeriod = period;
    },

    SET_MOBILE_POPUP_OPEN(state, status) {
      state.activeCoinsDetail.isMobilePopupOpen = status;
    },

    CLEAR_COIN_DETAILS(state) { 
      state.activeCoinsDetail.coinId = null;
      state.activeCoinsDetail.coinData = null;
      state.activeCoinsDetail.dataImpulse = [];
      state.activeCoinsDetail.dataPeriod = [];
      state.activeCoinsDetail.isMobilePopupOpen = false;
      state.ready.isCoinDetailsLoaded = false;
    },

    //Флаги загрузки
    SET_DYNAMICS_LOADING_FLAG(state, status) { state.loaders.dynamicsFilterLoading = status; },
    SET_IMPULSES_LOADING_FLAG(state, status) { state.loaders.impulsesFilterLoading = status; },
    SET_CHART_DATA_LOADING_FLAG(state, status) { state.loaders.chartDataLoading = status; },
    SET_COINS_LOADING_FLAG(state, status) { state.loaders.coinsLoading = status; },
    SET_LOADING_NEXT_FLAG(state, status) { state.pagination.isLoadingNext = status; },
    SET_COIN_DETAILS_LOADING_FLAG(state, status) { state.loaders.isCoinDetailsLoading = status; },

    //Флаги готовности
    SET_READY_CHART_FLAG(state, status) { state.ready.isReadyChart = status; },
    SET_READY_COINS_FLAG(state, status) { state.ready.isReadyCoins = status; },
    SET_COIN_DETAILS_LOADED_FLAG(state, status) { state.ready.isCoinDetailsLoaded = status; },

    //Предвыбранный импульс для виджета
    SET_PRESELECTED_IMPULSE_ID(state, id) {
      state.preselectedImpulseId = id;
    },

    //Сброс стора в исходное
    RESET_STATE(state) {
      state.coins = [];
      state.chartData = [];
      
      state.activeCoinsDetail = {
        coinId: null,
        coinData: null,
        dataPeriod: [],
      };
      
      state.filter = {
        dynamicsList: [],
        impulsesList: [],
        baseCoinId: {
          global: 'BTC',
          coinDetails: 'USD',
        },
        impulse: {
          context: 'impulse',
          value: null,
        },
        category: 'crypto_100',
        isBriefcase: false,
      };
      
      state.pagination = {
        currentPage: 1,
        totalPages: 0,
        hasNextPage: false,
        isLoadingNext: false,
      };
      
      state.loaders = {
        dynamicsFilterLoading: false,
        impulsesFilterLoading: false,
        chartDataLoading: false,
        coinsLoading: false,
        isCoinDetailsLoading: false,
      };
      
      state.ready = {
        isReadyChart: false,
        isReadyCoins: false,
        isCoinDetailsLoaded: false,
      };

      state.preselectedImpulseId = null;
    },
  },

  getters: {
    //Монеты
    coinsList: (state) => state.coins,
    activeCoinDetail: (state) => state.activeCoinsDetail,
    isOpenCoinDetails: (state) => !!state.activeCoinsDetail.coinId,

    //Пагинация
    hasNextPage: (state) => state.pagination.hasNextPage,
    isLoadingNext: (state) => state.pagination.isLoadingNext,
    isCoinsLoading: (state) => state.loaders.coinsLoading,

    //Пустой портфель
    isEmptyBriefcaseList: (state) => {
      return state.filter.category === 'briefcase' && state.coins.length === 0;
    },

    isBriefcase: (state) => state.filter.isBriefcase,

    //Базовые ID монет
    baseCoinIdGlobal: (state) => state.filter.baseCoinId.global,
    baseCoinIdCoinDetails: (state) => state.filter.baseCoinId.coinDetails,

    //Категории
    activeCategory: (state) => state.filter.category,
    isCategoryActive: (state) => (categorySlug) => state.filter.category === categorySlug,

    //Импульсы (динамика/импульс)
    activeImpulse: (state) => state.filter.impulse,
    activeImpulseType: (state) => state.filter.impulse.context,
    activeImpulseValue: (state) => state.filter.impulse.value,
    isImpulseActive: (state) => ({ context, value }) => {
      return state.filter.impulse.context === context && state.filter.impulse.value === value;
    },

    //Тип текущего фильтра
    isCurrentFilterImpulse: (state) => state.filter.impulse.context === 'impulse',
    isCurrentFilterDynamic: (state) => state.filter.impulse.context === 'dynamic',

    //Объект текущего фильтра
    currentFilterObject: (state) => {
      const context = state.filter.impulse.context;
      const value = state.filter.impulse.value;

      if (context === 'dynamic') {
        return state.filter.dynamicsList.find(d => d.period === value) || null;
      } else if (context === 'impulse') {
        return state.filter.impulsesList.find(i => i.impulse_id === parseInt(value)) || null;
      }

      return null;
    },

    //Списки фильтров
    dynamicsList: (state) => state.filter.dynamicsList,
    impulsesList: (state) => state.filter.impulsesList,

    //Данные графика
    chartData: (state) => state.chartData,

    //Детали монеты
    coinDetailsData: (state) => state.activeCoinsDetail.coinData,
    coinDetailsPeriod: (state) => state.activeCoinsDetail.dataPeriod,
    isCoinDetailsOpen: (state) => !!state.activeCoinsDetail.coinId,

    //Загрузки
    isDynamicsFilterLoading: (state) => state.loaders.dynamicsFilterLoading,
    impulsesFilterLoading: (state) => state.loaders.impulsesFilterLoading,
    isCoinDetailsLoading: (state) => state.loaders.isCoinDetailsLoading,
    isChartDataLoading: (state) => state.loaders.chartDataLoading,

    //Флаги готовности
    isReadyChart: (state) => state.ready.isReadyChart,
    isReadyCoins: (state) => state.ready.isReadyCoins,
    isCoinDetailsLoaded: (state) => state.ready.isCoinDetailsLoaded,

    //Предвыбранный импульс
    preselectedImpulseId: (state) => state.preselectedImpulseId,

    isMobilePopupOpen: (state) => state.activeCoinsDetail.isMobilePopupOpen,
  },
};

export default Scanner;